'use client';

import { useMemo, useState } from 'react';
import { PROGRAM_TEMPLATE_DEFINITIONS } from '@court-sync/shared';
import type { AcademySport, ProgramTemplateProviderId } from '@court-sync/shared';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui';
import { ProgramTemplateProviderCard } from './ProgramTemplateProviderCard';
import { SportFilterBadges } from '@/components/sport/SportFilterBadges';
import { getActiveAcademySports, getSportLabel } from '@/lib/sportPresentation';

export function ProgramPathwayChoiceModal({
  isOpen,
  onClose,
  onSelectPredefined,
  onSelectCustom,
  academySports,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelectPredefined: (templateId: ProgramTemplateProviderId) => void;
  onSelectCustom: () => void;
  academySports: AcademySport[];
}) {
  const [sportFilter, setSportFilter] = useState('');

  const activeSports = useMemo(() => getActiveAcademySports(academySports), [academySports]);

  const activeSportIds = useMemo(() => new Set(activeSports.map((s) => s.sportId)), [activeSports]);

  const eligibleTemplates = useMemo(
    () => PROGRAM_TEMPLATE_DEFINITIONS.filter((t) => activeSportIds.has(t.sportId)),
    [activeSportIds]
  );

  const templatesWithSport = useMemo(
    () =>
      eligibleTemplates.map((template) => {
        const academySport = activeSports.find((s) => s.sportId === template.sportId);
        return {
          template,
          academySport,
          sportLabel: academySport ? getSportLabel(academySport) : undefined,
        };
      }),
    [eligibleTemplates, activeSports]
  );

  const visibleTemplates = useMemo(() => {
    if (!sportFilter) return templatesWithSport;
    return templatesWithSport.filter((entry) => entry.academySport?.id === sportFilter);
  }, [templatesWithSport, sportFilter]);

  const sportsWithTemplates = useMemo(() => {
    const sportIdsWithTemplates = new Set(eligibleTemplates.map((t) => t.sportId));
    return activeSports.filter((s) => sportIdsWithTemplates.has(s.sportId));
  }, [activeSports, eligibleTemplates]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New program pathway" maxWidth="3xl">
      <div className="space-y-6">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Use a reference development layout or create a custom pathway from scratch.
        </p>
        {eligibleTemplates.length === 0 ? (
          <p className="text-sm text-amber-700 dark:text-amber-300 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3">
            No reference templates match your sports yet. Create a custom pathway instead.
          </p>
        ) : null}
        {sportsWithTemplates.length > 1 ? (
          <SportFilterBadges
            sports={sportsWithTemplates}
            value={sportFilter}
            onChange={setSportFilter}
            ariaLabel="Filter templates by sport"
          />
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          {visibleTemplates.map(({ template, sportLabel }) => (
            <ProgramTemplateProviderCard
              key={template.templateId}
              selection={template.templateId}
              selected={false}
              title={template.templateName}
              description={template.shortDescription}
              regionLabel={template.regionLabel}
              sportLabel={sportLabel}
              onSelect={() => onSelectPredefined(template.templateId)}
            />
          ))}
          <ProgramTemplateProviderCard
            selection="custom"
            selected={false}
            title="Create custom pathway"
            description="Start from scratch and define your own program stages manually."
            onSelect={onSelectCustom}
          />
        </div>
        <div className="flex justify-end pt-2 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
